import { Alert, AlertDescription, AlertIcon, Box, Button, Flex, SimpleGrid, useDisclosure } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { mappingKhmerNumber } from '../../../utils/market-development';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useState } from 'react';
import CompanyTypeField from './ews-company-type-field';
import CompanyField from './ews-company-field';
import SelectValueFormField from '../../../components/form/SelectValueFormField';
import useStore from '../../../store';
import c from '../../../constant';
import * as Yup from 'yup';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/km';

const CREATE_RATIO_DOCUMENT = gql`
	mutation CreateAnnuallyFinancialStatementsRatio(
		$companyId: String!
		$companyType: InsuranceInstitutionsType!
		$year: Float!
		$templates: [EWSRatioTemplateInput!] = []
	) {
		createAnnuallyFinancialStatementsRatio(companyId: $companyId, companyType: $companyType, year: $year, templates: $templates)
	}
`;

const Q_CHECK_LIFE_DUPLICATE = gql`
	query ($companyId: String!, $year: Float!) {
		checkDuplicatedLifeAnnualRatioReport(companyId: $companyId, year: $year)
	}
`;

const Q_CHECK_NON_LIFE_DUPLICATE = gql`
	query ($companyId: String!, $year: Float!) {
		checkDuplicatedNonLifeAnnualRatioReport(companyId: $companyId, year: $year)
	}
`;

const validationSchema = Yup.object().shape({
	companyType: Yup.string().nullable().required('Required'),
	companyId: Yup.string().nullable().required('Required'),
	year: Yup.string().nullable().required('Required'),
});

export default function CreateRatioDocument({ period = null, reportId = null, companyType = null, companyId = null, year = null, onCallback = () => null }) {
	const { t } = useTranslation();
	const router = useHistory();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [errorMessage, setErrorMessage] = useState(null);
	const [createRatioDocument, { data, loading, error }] = useMutation(CREATE_RATIO_DOCUMENT);
	const [checkDuplicateLifeStatement, { data: dataDuplicateLife }] = useLazyQuery(Q_CHECK_LIFE_DUPLICATE, { fetchPolicy: 'no-cache' });
	const [checkDuplicateNonLifeStatement, { data: dataDuplicateNonLife }] = useLazyQuery(Q_CHECK_NON_LIFE_DUPLICATE, { fetchPolicy: 'no-cache' });
	const [checkingDuplicate, setCheckingDuplicate] = useState(false);

	return (
		<>
			{errorMessage && (
				<Alert mb={4} status="error">
					<AlertIcon />
					<AlertDescription>{t(errorMessage)}</AlertDescription>
				</Alert>
			)}
			<Formik
				validationSchema={validationSchema}
				initialValues={{
					companyType,
					companyId,
					year,
				}}
				onSubmit={async (values) => {
					setErrorMessage(null);

					try {
						if (!values?.companyId || !values?.companyType || !Number(values?.year)) {
							setErrorMessage('Please select company type, company and year');
							return;
						}

						setCheckingDuplicate(true);

						let duplicateRes;
						let duplicated = false;

						if (values.companyType === 'LIFE') {
							duplicateRes = await checkDuplicateLifeStatement({
								variables: {
									companyId: values.companyId,
									year: Number(values?.year),
								},
							});
							duplicated = duplicateRes?.data?.checkDuplicatedLifeAnnualRatioReport?.duplicated;
						} else {
							duplicateRes = await checkDuplicateNonLifeStatement({
								variables: {
									companyId: values.companyId,
									year: Number(values?.year),
								},
							});
							duplicated = duplicateRes?.data?.checkDuplicatedNonLifeAnnualRatioReport?.duplicated;
						}

						if (duplicated) {
							setErrorMessage('Annual financial statement ratio for this company and year already exists');
							return;
						}

						const res = await createRatioDocument({
							variables: {
								companyId: values?.companyId,
								companyType: values?.companyType,
								year: Number(values?.year),
							},
						});

						const id = res?.data?.createAnnuallyFinancialStatementsRatio;
						if (!id) throw new Error('Failed to get statement ID after creation');

						router.push(
							`/early-warning-system/annual-financial-statement-ratio/detail?statementId=${id}&year=${values?.year}&companyId=${values?.companyId}&companyType=${values?.companyType}&period=${period}&report=ews`
						);
						onCallback(id);
						onClose();
					} catch (error) {
						console.error('Failed to create ratio document:', error);
						setErrorMessage(error?.message || 'Failed to create ratio document');
					} finally {
						setCheckingDuplicate(false);
					}
				}}
			>
				{({ values, setFieldValue, ...props }) => {
					return (
						<Box pb="4">
							<Form>
								<SimpleGrid columns={1} spacing={4}>
									<CompanyTypeField
										{...props}
										name="companyType"
										companyFormName="companyId"
										label="Company Type"
										placeholder={t('Company Type')}
									/>
									<CompanyField {...props} name="companyId" companyTypeFormName="companyType" label="Company" />
									<SelectValueFormField
										{...props}
										placeholder={t('Year')}
										name="year"
										label="Year"
										options={[0].reduce((p, c) => {
											let lp = p;
											let i = 2021;
											while (i <= moment().year()) {
												lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : i, value: i });
												i = i + 1;
											}
											return lp?.reverse();
										}, [])}
									/>
									<Flex mt={2} justifyContent="flex-end">
										<Button
											mr={2}
											colorScheme="gray"
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											borderRadius={c.borderRadius}
											onClick={() => {
												onCallback('?period=' + period + '&reportId=' + reportId + '&page=1&report=ews');
												onClose();
											}}
										>
											{t('Clear')}
										</Button>
										<Button
											ml={2}
											colorScheme="blue"
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											type="submit"
											borderRadius={c.borderRadius}
											loading={checkingDuplicate}
										>
											{t('Create')}
										</Button>
									</Flex>
								</SimpleGrid>
							</Form>
						</Box>
					);
				}}
			</Formik>
		</>
	);
}
